import _ from 'lodash'
import RequestError from './RequestError'
import api from '@/api'

// Moderation for the realtime audience room (chat / Q&A / polls / reactions).
//
// Every call goes to the Castr backend, which proxies to the castr-audience
// worker with the service secret. The dashboard never talks to the worker's
// admin API directly, so ownership checks and audit logging stay in one place.
// The only direct connection to the worker is the read/moderate WebSocket,
// which uses the short-lived host token from `getRoomToken`.
export default {
	getRoomToken,
	getStats,
	getExport,
	getArchive,
	updateRoomConfig,

	openPoll,
	closePoll,

	deleteMessage,
	clearMessages,
	pinMessage,
	unpinMessage,

	listBans,
	banViewer,
	unbanViewer,

	markQuestionAnswered,
	deleteQuestion,

	listCaptures,
	getCapturesCsv,
	deleteCapture,
	getCapturesWebhook,
	setCapturesWebhook,

	setCta,
	removeCta,

	purgeRoom
}

/**
 * Mints a host/mod room token so the dashboard can join the room it owns.
 * @param {string} streamId
 * @param {'host'|'mod'} role
 */
function getRoomToken(streamId, role = 'host') {
	return makeRequest(`/streams/${streamId}/audience/token?role=${role}`)
}

function getStats(streamId) {
	return makeRequest(`/streams/${streamId}/audience/stats`)
}

/** Full Q&A and poll results with tallies. Chat is not included. */
function getExport(streamId) {
	return makeRequest(`/streams/${streamId}/audience/export`)
}

/**
 * Poll and Q&A results the archival webhook kept after the room itself moved
 * on. Rooms wipe themselves after 7 days idle and a purge drops everything, so
 * this is the only place old results survive.
 *
 * Answers with an empty record — `{ room, updatedAt: null, endedAt: null,
 * polls: [], questions: [], stats: null }` — rather than 404 when nothing was
 * ever archived, which is the normal case until the worker has an archival
 * webhook configured.
 */
function getArchive(streamId) {
	return makeRequest(`/streams/${streamId}/audience/archive`)
}

/**
 * Partial patch: slowModeSec, profanityFilter, blockLinks. This is how slow mode
 * changes for a room that is already live, since viewer tokens are minted at
 * page render and cannot be reissued to connected viewers.
 */
function updateRoomConfig(streamId, patch) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/config`,
		data: patch
	})
}

/**
 * @param {object} poll question, options (2-6), optional durationSec
 */
function openPoll(streamId, poll) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/polls`,
		data: poll
	})
}

function closePoll(streamId, pollId) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/polls/${pollId}/close`,
		data: {}
	})
}

function deleteMessage(streamId, messageId) {
	return makeRequest({
		method: 'delete',
		path: `/streams/${streamId}/audience/messages/${messageId}`
	})
}

/**
 * Clears chat and nothing else: questions, polls, votes, bans and config all
 * survive and nobody is disconnected. The room broadcasts `chat.clear` so every
 * connected client wipes what it is rendering.
 *
 * @returns {Promise<{ cleared: true, count: number }>} count is how many
 *   messages were on screen before the wipe.
 */
function clearMessages(streamId) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/messages/clear`,
		data: {}
	})
}

function pinMessage(streamId, messageId) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/messages/${messageId}/pin`,
		data: {}
	})
}

function unpinMessage(streamId, messageId) {
	return makeRequest({
		method: 'delete',
		path: `/streams/${streamId}/audience/messages/${messageId}/pin`
	})
}

/**
 * Bans still in force, newest first. Expired ones are filtered out server-side,
 * and the IP hash recorded alongside a ban is never returned.
 *
 * @returns {Promise<{ bans: Array<{ vid: string, until: number|null }> }>}
 *   `until` is an epoch ms expiry, or null for a permanent ban.
 */
function listBans(streamId) {
	return makeRequest(`/streams/${streamId}/audience/bans`)
}

/**
 * @param {number} [minutes] omit for a permanent ban
 */
function banViewer(streamId, viewerId, minutes) {
	const data = { viewerId }
	if (_.isFinite(minutes)) data.minutes = minutes

	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/bans`,
		data
	})
}

function unbanViewer(streamId, viewerId) {
	return makeRequest({
		method: 'delete',
		path: `/streams/${streamId}/audience/bans/${encodeURIComponent(viewerId)}`
	})
}

/**
 * Emails viewers left through the capture form, newest first.
 *
 * @param {{ limit?: number, skip?: number }} [page]
 * @returns {Promise<{ total: number, captures: Array<{ email: string,
 *   name: string, consent: boolean, firstSeen: number, lastSeen: number,
 *   vid: string }> }>}
 */
function listCaptures(streamId, page = {}) {
	const query = new URLSearchParams()
	if (_.isFinite(page.limit)) query.set('limit', page.limit)
	if (_.isFinite(page.skip)) query.set('skip', page.skip)

	const qs = query.toString()
	return makeRequest(`/streams/${streamId}/audience/captures${qs ? `?${qs}` : ''}`)
}

/**
 * The capture list as CSV, fetched through the axios instance rather than a
 * bare link so the `auth-token` header travels with it. The response is kept
 * as the raw string — axios would otherwise try JSON first — for the caller to
 * hand to a Blob.
 *
 * @returns {Promise<string>}
 */
function getCapturesCsv(streamId) {
	return makeRequest({
		path: `/streams/${streamId}/audience/captures/export`,
		responseType: 'text',
		transformResponse: [(data) => data]
	})
}

/**
 * Erases every capture of one email on this stream — the per-stream GDPR
 * lever. Irreversible.
 *
 * @returns {Promise<{ deleted: number }>}
 */
function deleteCapture(streamId, email) {
	return makeRequest({
		method: 'delete',
		path: `/streams/${streamId}/audience/captures?email=${encodeURIComponent(email)}`
	})
}

/**
 * @returns {Promise<{ url: string, secretSet: boolean }>} The secret is
 *   write-only and never comes back; `secretSet` is all the UI gets.
 */
function getCapturesWebhook(streamId) {
	return makeRequest(`/streams/${streamId}/audience/captures/webhook`)
}

/**
 * @param {{ url: string, secret?: string }} config An empty url clears the
 *   webhook. An empty secret leaves any stored one alone.
 */
function setCapturesWebhook(streamId, config) {
	return makeRequest({
		method: 'put',
		path: `/streams/${streamId}/audience/captures/webhook`,
		data: config
	})
}

/**
 * Sets the pinned call-to-action card, replacing any current one. The room
 * broadcasts `cta.set` to every viewer at once.
 *
 * @param {{ label: string, buttonText: string, url: string, color?: string }} cta
 *   url must be https; the worker rejects anything else with `invalid_url`.
 * @returns {Promise<{ cta: object }>}
 */
function setCta(streamId, cta) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/cta`,
		data: cta
	})
}

/** @returns {Promise<{ cta: null }>} Broadcasts the removal to every viewer. */
function removeCta(streamId) {
	return makeRequest({
		method: 'delete',
		path: `/streams/${streamId}/audience/cta`
	})
}

function markQuestionAnswered(streamId, questionId) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/questions/${questionId}/answered`,
		data: {}
	})
}

function deleteQuestion(streamId, questionId) {
	return makeRequest({
		method: 'delete',
		path: `/streams/${streamId}/audience/questions/${questionId}`
	})
}

/** Irreversible: disconnects everyone and drops all room state. */
function purgeRoom(streamId) {
	return makeRequest({
		method: 'post',
		path: `/streams/${streamId}/audience/purge`,
		data: {}
	})
}

async function makeRequest(reqConfig) {
	if (typeof reqConfig === 'string') {
		reqConfig = {
			path: reqConfig
		}
	}

	reqConfig.url = reqConfig.path

	let res
	try {
		res = await api.request(reqConfig)
	} catch (err) {
		const edata = _.get(err, 'response.data')
		throw new RequestError(edata)
	}

	return res.data
}
